import { useEffect, useState } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Eye, EyeOff, Save, User, Building2, ShieldCheck, Bell } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AuthGate from '../components/AuthGate';
import { KiuSettings } from '../components/KiuSettings';
import { VideoSettings } from '../components/SectionBackground';
import { MotionSettings } from '../components/StageMotion';
import { IdentityEditor } from '../components/IdentityEditor';
import { MyApplications } from '../components/MyApplications';
import { Notifications } from '../components/Notifications';
import { AvatarUploader } from '../components/Avatar';
import { PageHeader } from '../components/SectionHeader';
import { useI18n } from '../i18n';
import { useAuth } from '../auth/AuthContext';
import { CATEGORIES } from '../data/demo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FIELDS = [
  { key: 'role', type: 'text' },
  { key: 'category', type: 'select' },
  { key: 'location', type: 'text' },
  { key: 'experience', type: 'text' },
  { key: 'availability', type: 'availability' },
  { key: 'bio', type: 'textarea' },
  { key: 'phone', type: 'text' },
  { key: 'email', type: 'text' },
  { key: 'skills', type: 'csv' },
  { key: 'certifications', type: 'csv' },
];

const COMPANY_FIELDS = [
  { key: 'company_name', label: 'Nombre de la empresa', required: true, placeholder: 'LumenTech Escénica S.L.' },
  { key: 'company_tax_id', label: 'CIF / NIF', required: true, placeholder: 'B12345678' },
  { key: 'company_email', label: 'Email de contacto', required: true, placeholder: 'contacto@empresa.com' },
  { key: 'company_phone', label: 'Teléfono', required: true, placeholder: '+34 934 000 000' },
  { key: 'company_address', label: 'Dirección', required: true, placeholder: 'C/ Ejemplo 1, Barcelona' },
  { key: 'company_website', label: 'Web', required: false, placeholder: 'https://empresa.com' },
  { key: 'company_sector', label: 'Sector / especialidad', required: false, placeholder: 'Iluminación y vídeo' },
];
const REQUIRED_COMPANY = COMPANY_FIELDS.filter((f) => f.required).map((f) => f.key);

const MyProfileInner = () => {
  const { t } = useI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const tab = ['perfil', 'configuracion', 'candidaturas', 'avisos'].includes(params.get('tab')) ? params.get('tab') : ({ '#kiu-ajustes': 'configuracion', '#candidaturas': 'candidaturas', '#avisos': 'avisos' }[location.hash] || 'perfil');
  const { checkAuth } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API}/profile/me`, { credentials: 'include' });
        if (res.status === 401) { navigate('/login'); return; }
        const data = await res.json();
        setProfile(data);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [navigate]);

  const update = (key, value) => setProfile((p) => ({ ...p, [key]: value }));
  const toggleVis = (key) => setProfile((p) => ({ ...p, visibility: { ...p.visibility, [key]: !p.visibility[key] } }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        account_type: profile.account_type || 'professional',
        role: profile.role || null,
        category: profile.category || null,
        location: profile.location || null,
        experience: profile.experience || null,
        availability: profile.availability || 'available',
        bio: profile.bio || null,
        phone: profile.phone || null,
        email: profile.email || null,
        skills: profile.skills || [],
        certifications: profile.certifications || [],
        company_name: profile.company_name || null,
        company_tax_id: profile.company_tax_id || null,
        company_email: profile.company_email || null,
        company_phone: profile.company_phone || null,
        company_website: profile.company_website || null,
        company_address: profile.company_address || null,
        company_sector: profile.company_sector || null,
        company_description: profile.company_description || null,
        notify_offers: !!profile.notify_offers,
        visibility: profile.visibility,
      };
      const res = await fetch(`${API}/profile/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('save failed');
      const data = await res.json();
      setProfile(data);
      toast.success(t('profile.saved'));
    } catch {
      toast.error(t('profile.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-[#0B0C0E]">
        <Navbar />
        <div className="max-w-3xl mx-auto px-4 py-24 text-center">
          <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
        <Footer />
      </div>
    );
  }

  const renderInput = (field) => {
    const v = profile[field.key];
    const common = 'w-full bg-[#0B0C0E] border border-[#2A2F3D] focus:border-amber-500 outline-none rounded-lg px-4 py-2.5 text-white text-sm';
    if (field.type === 'text') return (
      <input data-testid={`profile-${field.key}-input`} value={v || ''} onChange={(e) => update(field.key, e.target.value)} className={common} />
    );
    if (field.type === 'textarea') return (
      <textarea data-testid={`profile-${field.key}-input`} value={v || ''} onChange={(e) => update(field.key, e.target.value)} rows={4} className={common + ' resize-none'} />
    );
    if (field.type === 'select') return (
      <select data-testid={`profile-${field.key}-input`} value={v || ''} onChange={(e) => update(field.key, e.target.value)} className={common}>
        <option value="">—</option>
        {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
    );
    if (field.type === 'availability') return (
      <select data-testid={`profile-${field.key}-input`} value={v || 'available'} onChange={(e) => update(field.key, e.target.value)} className={common}>
        <option value="available">Disponible</option>
        <option value="onTour">En bolos / gira</option>
      </select>
    );
    if (field.type === 'csv') {
      const isCerts = field.key === 'certifications';
      return (
        <CsvInput
          testId={`profile-${field.key}-input`}
          value={v || []}
          onChange={(arr) => update(field.key, arr)}
          className={common}
          placeholder={isCerts ? 'IRATA Level 3, Dante Certified, PRL 60h' : 'GrandMA3, DMX, Wysiwyg'}
        />
      );
    }
    return null;
  };

  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <PageHeader title={t('profile.title')} subtitle={t('profile.subtitle')} testId="my-profile-header" />
        <div role="tablist" aria-label="Mi perfil" className="flex flex-wrap gap-2 mb-6" data-testid="profile-tabs">
          {[['perfil', 'Perfil profesional'], ['candidaturas', 'Mis candidaturas'], ['avisos', 'Avisos'], ['configuracion', 'Configuración']].map(([id, label]) => <button key={id} id={`profile-tab-${id}`} role="tab" aria-selected={tab === id} aria-controls={`profile-panel-${id}`} data-testid={`profile-tab-${id}`} onClick={() => setParams(id === 'perfil' ? {} : { tab: id })} className={`min-h-11 px-5 py-2 rounded-full text-sm font-semibold border transition-colors sc-focus ${tab === id ? 'bg-amber-500 border-amber-500 text-slate-950' : 'border-[#2A2F3D] text-slate-300 hover:border-amber-500/50'}`}>{label}</button>)}
        </div>

        <div className="sc-card p-6 md:p-8 mb-6" data-testid="profile-name-card">
          <div className="flex items-center gap-4">
            <AvatarUploader src={profile.avatar_url} name={profile.nick || profile.name} onUploaded={(url) => { setProfile((p) => ({ ...p, avatar_url: url })); checkAuth(); }} />
            <div className="min-w-0">
              <div className="sc-mono-eyebrow text-slate-500 mb-1">Cuenta</div>
              <div className="text-white text-lg font-semibold truncate" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="profile-display-name">{profile.nick || profile.name}</div>
              <div className="text-xs text-amber-500/80 font-mono" data-testid="profile-display-handle">{profile.handle ? `@${profile.handle}` : 'Sin ID todavía · configúralo abajo'}</div>
            </div>
          </div>
        </div>

        {tab === 'configuracion' && <div role="tabpanel" id="profile-panel-configuracion" aria-labelledby="profile-tab-configuracion" data-testid="profile-config-panel">
          <KiuSettings />
          <VideoSettings />
          <MotionSettings />
          <IdentityEditor profile={profile} onSaved={(data) => { setProfile((p) => ({ ...p, handle: data.handle, nick: data.nick })); checkAuth(); }} />
          <section className="sc-card p-6 md:p-8 mb-6" data-testid="profile-privacy-settings">
            <h2 className="text-base font-semibold text-white mb-2">Privacidad del perfil</h2>
            <p className="text-sm text-slate-400 mb-5">Elige qué información pueden ver los demás miembros. Pulsa «Guardar cambios» para aplicar la privacidad.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{FIELDS.map((field) => {
              const on = !!profile.visibility?.[field.key];
              return <div key={field.key} className="flex justify-between items-center gap-3 border border-[#2A2F3D] rounded-lg p-3"><span className="text-sm text-slate-300">{t(`profile.field.${field.key}`)}</span><button type="button" role="switch" aria-checked={on} aria-label={`Visibilidad: ${t(`profile.field.${field.key}`)}`} data-testid={`vis-toggle-${field.key}`} onClick={() => toggleVis(field.key)} className={`text-xs min-h-11 flex items-center gap-2 px-3 py-2 border rounded-md transition-colors sc-focus ${on ? 'text-emerald-300 border-emerald-500/50' : 'text-red-300 border-red-500/40'}`}>{on ? <Eye size={14} /> : <EyeOff size={14} />}{on ? 'Visible' : 'Oculto'}</button></div>;
            })}</div>
          </section>
        </div>}
        {tab === 'candidaturas' && <div role="tabpanel" id="profile-panel-candidaturas" aria-labelledby="profile-tab-candidaturas" data-testid="profile-applications-panel"><MyApplications /></div>}
        {tab === 'avisos' && <div role="tabpanel" id="profile-panel-avisos" aria-labelledby="profile-tab-avisos" data-testid="profile-notifications-panel"><Notifications /></div>}
        {tab === 'perfil' && <div role="tabpanel" id="profile-panel-perfil" aria-labelledby="profile-tab-perfil" data-testid="profile-professional-panel">
        <div className="sc-card p-6 md:p-8 mb-6" data-testid="account-type-card">
          <h2 className="sc-mono-eyebrow text-amber-500 mb-2">Tipo de cuenta</h2>
          <p className="text-sm text-slate-400 mb-5 leading-relaxed">Elige cómo quieres usar Stage Crew. Puedes cambiarlo cuando quieras.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[['professional', 'Profesional / Trabajador', 'Recibes ofertas y publicas como profesional.', User], ['company', 'Empresa', 'Publicas ofertas a nombre de tu empresa.', Building2]].map(([id, title, desc, Icon]) => {
              const active = (profile.account_type || 'professional') === id;
              return (
                <button key={id} type="button" data-testid={`account-type-${id}`} onClick={() => update('account_type', id)}
                  className={`text-left sc-card p-4 transition-all ${active ? 'border-amber-500/60 shadow-[0_0_0_1px_rgba(245,158,11,0.4)]' : ''}`}>
                  <div className="flex items-center gap-2 mb-1"><Icon size={18} className="text-amber-500" /><span className="text-sm font-semibold text-white">{title}</span></div>
                  <span className="text-xs text-slate-400">{desc}</span>
                </button>
              );
            })}
          </div>
          <p className="text-[11px] text-slate-500 mt-3">Recuerda pulsar «Guardar cambios» para aplicar el tipo de cuenta.</p>
        </div>

        {(profile.account_type || 'professional') === 'company' ? (
          <div className="sc-card p-6 md:p-8 mb-6" data-testid="company-data-card">
            <h2 className="sc-mono-eyebrow text-amber-500 mb-4">Datos de empresa</h2>
            {profile.company_verified ? (
              <div className="flex items-center gap-2 mb-6 rounded-lg border border-emerald-500/40 bg-emerald-500/5 px-4 py-3" data-testid="company-verified-banner">
                <ShieldCheck size={18} className="text-emerald-400 shrink-0" />
                <span className="text-sm text-emerald-300">Empresa <strong>Verificada</strong>. Apareces con el sello de confianza en el directorio y en tus ofertas.</span>
              </div>
            ) : (
              <div className="mb-6 rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-3" data-testid="company-unverified-banner">
                <p className="text-sm text-amber-300 leading-relaxed">Completa los campos obligatorios (*) y guarda para aparecer como <strong>«Verificada»</strong> y que la gente confíe y pueda contactarte.</p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {COMPANY_FIELDS.map((field) => (
                <div key={field.key} data-testid={`profile-field-${field.key}`}>
                  <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">{field.label}{field.required && <span className="text-amber-500"> *</span>}</label>
                  <input data-testid={`profile-${field.key}-input`} value={profile[field.key] || ''} onChange={(e) => update(field.key, e.target.value)} placeholder={field.placeholder}
                    className="w-full bg-[#0B0C0E] border border-[#2A2F3D] focus:border-amber-500 outline-none rounded-lg px-4 py-2.5 text-white text-sm" />
                </div>
              ))}
              <div className="md:col-span-2" data-testid="profile-field-company_description">
                <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">Descripción</label>
                <textarea data-testid="profile-company_description-input" value={profile.company_description || ''} onChange={(e) => update('company_description', e.target.value)} rows={4} placeholder="A qué se dedica tu empresa…"
                  className="w-full bg-[#0B0C0E] border border-[#2A2F3D] focus:border-amber-500 outline-none rounded-lg px-4 py-2.5 text-white text-sm resize-none" />
              </div>
            </div>
            {!profile.company_verified && (
              <p className="text-[11px] text-slate-500 mt-4">Faltan por completar: {REQUIRED_COMPANY.filter((k) => !String(profile[k] || '').trim()).map((k) => COMPANY_FIELDS.find((f) => f.key === k).label).join(', ') || 'ninguno · guarda para verificar'}.</p>
            )}
          </div>
        ) : (
          <div className="sc-card p-6 md:p-8 mb-6">
            <h2 className="sc-mono-eyebrow text-amber-500 mb-6">Perfil profesional</h2>
            <p className="text-sm text-slate-400 mb-6 leading-relaxed">Completa tu experiencia y especialidades. Puedes elegir qué datos son visibles en la pestaña Configuración.</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {FIELDS.map((field) => (
                <div key={field.key} data-testid={`profile-field-${field.key}`}>
                  <label className="block text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">{t(`profile.field.${field.key}`)}</label>
                  {renderInput(field)}
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-start justify-between gap-4 border border-[#2A2F3D] rounded-lg p-4" data-testid="notify-offers-card">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-sm font-semibold text-white"><Bell size={15} className="text-amber-500 shrink-0" />Avisarme de ofertas de mi especialidad</div>
                <p className="text-xs text-slate-400 mt-1 leading-relaxed">Recibirás un email cuando se publique una oferta nueva de tu categoría ({profile.category || 'sin categoría'}). Puedes desactivarlo cuando quieras.</p>
              </div>
              <button type="button" role="switch" aria-checked={!!profile.notify_offers} aria-label="Avisarme de ofertas de mi especialidad" data-testid="notify-offers-toggle" onClick={() => update('notify_offers', !profile.notify_offers)}
                className={`shrink-0 text-xs min-h-11 flex items-center gap-2 px-3 py-2 border rounded-md transition-colors sc-focus ${profile.notify_offers ? 'text-emerald-300 border-emerald-500/50' : 'text-slate-400 border-[#2A2F3D]'}`}>{profile.notify_offers ? 'Activado' : 'Desactivado'}</button>
            </div>
          </div>
        )}
        </div>}

        {(tab === 'perfil' || tab === 'configuracion') && <div className="flex justify-end pb-16">
          <button
            data-testid="profile-save-btn"
            onClick={save}
            disabled={saving}
            className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-slate-950 font-semibold px-6 py-3 rounded-lg transition-colors"
          >
            <Save size={16} />
            {saving ? '...' : t('profile.save')}
          </button>
        </div>}
      </div>
      <Footer />
    </div>
  );
};

const MyProfilePage = () => (
  <AuthGate>
    <MyProfileInner />
  </AuthGate>
);

// Comma-separated list input that keeps the raw text while typing (so trailing commas work).
const CsvInput = ({ value, onChange, className, placeholder, testId }) => {
  const [raw, setRaw] = useState(value.join(', '));
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setRaw(value.join(', ')); }, [value, focused]);
  return (
    <input
      data-testid={testId}
      value={raw}
      onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); setRaw(value.join(', ')); }}
      onChange={(e) => { setRaw(e.target.value); onChange(e.target.value.split(',').map((s) => s.trim()).filter(Boolean)); }}
      className={className}
      placeholder={placeholder}
    />
  );
};

export default MyProfilePage;
